import type { Flag } from '@/state/flags/flag-types';
import type { FlagManager } from '@/state/flags/useFlag';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Archive, MoreHorizontal, Power, Rocket, Trash2 } from 'lucide-react';
import { cn } from 'helpers/cn';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/sonner';
import { ConfirmDialog } from '@/components/Dialogs/ConfirmDialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { publishSummary } from '@/state/flags/flag-status';
import { WorkspaceToolbarControls } from '../../DashboardLayout/WorkspacePanel';
import { RenameControl } from '../../Experiments/Details/RenameControl';
import { FlagKindChip } from '../FlagChips';
import { FLAG_COPY } from '../copy';

type FlagHeaderProps = { manager: FlagManager; flag: Flag };

type Dialog = 'publish' | 'turnOff' | 'archive' | 'delete' | null;

const COPY = FLAG_COPY.header;
const MENU_ITEM_CLASS = 'text-dim focus:bg-hover focus:text-foreground rounded-md text-meta';

/** The experiment header's Start and End buttons, sized and coloured the same. */
const PRIMARY_CLASS = 'bg-cta hover:bg-ctahover h-8 min-w-[132px] rounded-lg px-3 text-meta font-medium text-white';
const STOP_CLASS =
  'bg-danger-bg border-danger-line text-danger hover:bg-danger-bg h-8 min-w-[132px] rounded-lg border px-3 text-meta font-medium';

/**
 * The flag's name, with its rename pencil and kind, and the key and id under it, in the experiment
 * header's layout. The page's main action sits in the toolbar beside the breadcrumb, where Start
 * and End sit on an experiment: Publish on a draft, Turn off while anyone can get On, nothing on an
 * archived flag. Archive and Delete are in the menu after it.
 */
export const FlagHeader = ({ manager, flag }: FlagHeaderProps) => {
  const navigate = useNavigate();
  const [dialog, setDialog] = useState<Dialog>(null);
  const close = () => setDialog(null);

  const confirmWith = (action: () => Promise<boolean>, done: string, after?: () => void) => async () => {
    if (await action()) {
      close();
      toast.success(done);
      after?.();
    }
  };

  const PrimaryAction = manager.isDraft ? (
    <Button
      onClick={() => setDialog('publish')}
      disabled={!manager.canChange || manager.pending !== null}
      title={manager.canChange ? undefined : COPY.noChange}
      data-tour="flag-publish"
      className={PRIMARY_CLASS}
    >
      <Rocket className="size-3.5" strokeWidth={1.5} />
      {manager.pending === 'publish' ? COPY.publishing : COPY.publish}
    </Button>
  ) : manager.offersTurnOff ? (
    <Button
      variant="destructive"
      onClick={() => setDialog('turnOff')}
      disabled={!manager.canTurnOff || manager.pending !== null}
      title={manager.canChange ? undefined : COPY.noChange}
      data-tour="flag-turn-off"
      className={STOP_CLASS}
    >
      <Power className="size-3.5" strokeWidth={1.5} />
      {COPY.turnOff}
    </Button>
  ) : null;

  const MoreMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={COPY.moreOptions}
          className="text-dim hover:text-foreground hover:bg-hover size-8 rounded-lg"
        >
          <MoreHorizontal className="size-4" strokeWidth={1.5} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="border-line bg-panel rounded-lg">
        <DropdownMenuItem
          onClick={() => setDialog('archive')}
          disabled={manager.isArchived || !manager.canChange}
          className={MENU_ITEM_CLASS}
        >
          <Archive className="mr-2 size-3.5" strokeWidth={1.5} /> {COPY.archive}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => setDialog('delete')}
          disabled={!manager.isDraft || !manager.canEdit}
          className={cn(MENU_ITEM_CLASS, 'text-danger focus:text-danger')}
        >
          <Trash2 className="mr-2 size-3.5" strokeWidth={1.5} /> {COPY.delete}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  // The name is not what the SDK asks for (that is the key), so it can change at any stage but archived.
  const RenameAction =
    !manager.isArchived && manager.canEdit ? (
      <RenameControl name={flag.name} copy={COPY.rename} onRename={manager.rename} />
    ) : null;

  const kind = manager.rollout?.kind ?? 'code';
  const IdLine = (
    <span className="micro-label normal-case">
      <span className="text-foreground font-mono">{flag.flagKey}</span>
      <span className="mx-2">·</span>id {flag.id}
    </span>
  );

  return (
    <div className="flex flex-col gap-[14px]">
      {/* DESKTOP */}
      <div className="hidden min-w-0 flex-col gap-1.5 md:flex">
        <div className="flex min-w-0 items-center gap-3">
          <h1 className="text-foreground truncate text-[18px] leading-none font-medium" title={flag.name}>
            {flag.name}
          </h1>
          {RenameAction}
          <FlagKindChip kind={kind} />
        </div>
        {IdLine}
        <WorkspaceToolbarControls>
          <div className="order-last flex shrink-0 items-center gap-2">
            {PrimaryAction}
            {MoreMenu}
          </div>
        </WorkspaceToolbarControls>
      </div>

      {/* MOBILE: the toolbar is desktop only, so the actions stay on the page. */}
      <div className="flex flex-col gap-3 md:hidden">
        <div className="flex items-center justify-end">{MoreMenu}</div>
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-foreground min-w-0 truncate text-title leading-tight font-medium" title={flag.name}>
            {flag.name}
          </h1>
          {RenameAction}
          <FlagKindChip kind={kind} />
        </div>
        {/* Wraps rather than truncates: a flag key is long, and it is what your code asks for. */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="min-w-0 break-all">{IdLine}</span>
          {PrimaryAction && <div className="flex shrink-0">{PrimaryAction}</div>}
        </div>
      </div>

      <ConfirmDialog
        open={dialog === 'publish'}
        title={COPY.publishTitle}
        confirmLabel={COPY.publish}
        onConfirm={confirmWith(manager.publish, COPY.published)}
        onCancel={close}
      >
        <p className="supporting m-0">{publishSummary(flag)}</p>
      </ConfirmDialog>

      <ConfirmDialog
        open={dialog === 'turnOff'}
        title={COPY.turnOffTitle}
        confirmLabel={COPY.turnOff}
        confirmVariant="destructive"
        onConfirm={confirmWith(manager.turnOff, COPY.turnedOff)}
        onCancel={close}
      >
        <p className="supporting m-0">{COPY.turnOffBody}</p>
      </ConfirmDialog>

      <ConfirmDialog
        open={dialog === 'archive'}
        title={COPY.archiveTitle}
        confirmLabel={COPY.archive}
        confirmVariant="destructive"
        onConfirm={confirmWith(manager.archive, COPY.archived)}
        onCancel={close}
      >
        <p className="supporting m-0">{COPY.archiveBody}</p>
      </ConfirmDialog>

      <ConfirmDialog
        open={dialog === 'delete'}
        title={COPY.deleteTitle}
        confirmLabel={COPY.delete}
        confirmVariant="destructive"
        onConfirm={async () => {
          if (await manager.remove()) {
            close();
            navigate('/flags/library');
          }
        }}
        onCancel={close}
      >
        <p className="supporting m-0">{COPY.deleteBody}</p>
      </ConfirmDialog>
    </div>
  );
};
